/**
 * TimelineOrgTaskToggle Component
 *
 * Toggles visibility of org-level task rows on the timeline.
 * Disabled when viewStrategy === 'phases' (org task rows have no phases).
 * Hides automatically when a task is hovered (same pattern as ViewToggle).
 */

import React, { useEffect, useRef } from 'react'
import { Home, HomeOutlined } from '@mui/icons-material'
import { Stack, useMediaQuery, useTheme } from '@mui/material'
import { TpToggleButton } from '~/components/tp-toggle-button'
import { TpControlTooltip } from '../../../../../components/tp-control-tooltip'
import {
  useTimelineContextDispatch,
  setShowOrgTasks,
} from '../../context/timeline-context'
import { useTimelineStaticContext } from '../../context/timeline-static-context'
import { useOrgHoverContext } from '~/common/context/organization/org-context'
import { useEditCropContext } from '~/crop/edit-crop/edit-crop-context'
import { CONTROLS_BREAKPOINT } from './timeline-controls-constants'

export const TimelineOrgTaskToggle = () => {
  const theme = useTheme()
  const isExpanded = useMediaQuery(theme.breakpoints.up(CONTROLS_BREAKPOINT))
  const { showOrgTasks, viewStrategy } = useTimelineStaticContext()
  const dispatch = useTimelineContextDispatch()
  const { hoveredTask } = useOrgHoverContext()
  const editCtx = useEditCropContext()
  const isEditing = editCtx?.editingCropId !== null

  const prevShowOrgTasksRef = useRef(showOrgTasks)

  useEffect(() => {
    if (isEditing) return

    if (hoveredTask !== null) {
      dispatch(setShowOrgTasks({ showOrgTasks: false }))
    } else {
      dispatch(setShowOrgTasks({ showOrgTasks: prevShowOrgTasksRef.current ?? true }))
    }
  }, [dispatch, isEditing, hoveredTask])

  const handleToggle = () => {
    const next = !showOrgTasks
    prevShowOrgTasksRef.current = next
    dispatch(setShowOrgTasks({ showOrgTasks: next }))
  }

  const isDisabled = viewStrategy === 'phases'

  if (isExpanded) {
    return (
      <TimelineOrgTaskToggleContent
        showOrgTasks={showOrgTasks}
        isDisabled={isDisabled}
        handleToggle={handleToggle}
        sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
      />
    )
  }

  return (
    <TimelineOrgTaskToggleContent
      showOrgTasks={showOrgTasks}
      isDisabled={isDisabled}
      handleToggle={handleToggle}
    />
  )
}

const TimelineOrgTaskToggleContent = ({ showOrgTasks, isDisabled, handleToggle, ...rest }) => {
  return (
    <TpToggleButton
      disabled={isDisabled}
      onClick={handleToggle}
      selected={showOrgTasks}
      {...rest}
    >
      <TpControlTooltip title="Org Tasks" offsetBottom={-8}>
        <Stack>
          {showOrgTasks
            ? <Home sx={{ fontSize: 20 }} />
            : <HomeOutlined sx={{ fontSize: 20 }} />
          }
        </Stack>
      </TpControlTooltip>
    </TpToggleButton>
  )
}
